/**
 * Canonical KPA standfirst descriptions (Eduvos 4-pillar model).
 *
 * The KPA name comes from the backend, but the descriptive standfirst is not
 * stored — it used to be generated as a generic "Manage and optimise <name> to
 * achieve strategic objectives" line in several places. This is the single
 * source of truth for the real descriptions; getKpaDescription() falls back to
 * the generic line for any non-standard KPA.
 *
 * Keyed by the KPA name normalised (lowercased, "&"→"and", whitespace
 * collapsed) so minor naming variants ("Operating" vs "Operational", "&" vs
 * "and", trailing spaces) all resolve.
 */

const KPA_DESCRIPTIONS: Record<string, string> = {
  'operating and financial results':
    'Achieving core role outputs and results. Deliver targets and outputs. This KPA measures the core results an individual or team is accountable for — the numbers, the deliverables, the outcomes that matter most to the business. Focuses on meeting targets, managing budgets, and achieving core role outputs efficiently and effectively.',
  // Naming variant seen in some data ("Operational" vs "Operating").
  'operational and financial results':
    'Achieving core role outputs and results. Deliver targets and outputs. This KPA measures the core results an individual or team is accountable for — the numbers, the deliverables, the outcomes that matter most to the business. Focuses on meeting targets, managing budgets, and achieving core role outputs efficiently and effectively.',
  'internal business processes and enablement':
    'Plan, execute and govern work. This KPA captures how work gets done — the quality of planning, the rigour of execution and operational efficiency, and the discipline of governance and risk management.',
  'culture and people':
    'Reflects the human dimension of performance. Highlights culture and leadership behaviours, talent development, engagement, and fostering a healthy, high-performing team environment.',
  'customer and stakeholder relationships':
    'Focuses on building trust-based relationships that support organizational effectiveness and growth - the quality and depth of relationships — internal and external — that enable collaboration, trust and long-term value creation.',
}

const normalize = (name: string): string =>
  name.trim().toLowerCase().replace(/&/g, 'and').replace(/\s+/g, ' ')

/**
 * The canonical standfirst for a KPA, or the generic fallback for non-standard
 * KPAs. Pass the KPA's display name (or name).
 */
export function getKpaDescription(name: string | null | undefined): string {
  const key = normalize(name || '')
  return (
    KPA_DESCRIPTIONS[key] ||
    `Manage and optimise ${(name || '').toLowerCase()} to achieve strategic objectives`
  )
}
